import React from "react";
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery,
  Divider,
  Grid,
} from "@mui/material";
import {
  QuizOutlined,
  CheckCircleOutline,
  Speed,
  Person,
  Person2,
  Scoreboard,
} from "@mui/icons-material";
import { Tables } from "../../database.types";
import { getClosestGuessBonusRoundScoreDB } from "../utils/utils";

interface ClosestGuessBonusRoundDisplayProps {
  bonusRound: Tables<"bonus_rounds">;
  closestGuessQuestions: Tables<"closest_guess_questions">[];
  team1Name: string;
  team2Name: string;
}

const ClosestGuessBonusRoundPlayer: React.FC<
  ClosestGuessBonusRoundDisplayProps
> = ({ bonusRound, closestGuessQuestions, team1Name, team2Name }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const sortedQuestions = [...closestGuessQuestions].sort(
    (a, b) => a.question_order - b.question_order,
  );

  if (bonusRound.current_question_idx === null) {
    return (
      <Typography color="error" align="center">
        No current question selected
      </Typography>
    );
  }

  const currentQuestion = sortedQuestions[bonusRound.current_question_idx];
  console.log(sortedQuestions);
  console.log(currentQuestion);
  console.log(bonusRound.current_question_idx);
  if (currentQuestion === undefined) {
    return (
      <Typography color="error" align="center">
        Invalid question index
      </Typography>
    );
  }

  // Calculate differences and determine winner
  let team1Difference: string = "N/A";
  let team2Difference: string = "N/A";
  let winner: 1 | 2 | null = null;

  if (currentQuestion.correct_answer !== null) {
    if (currentQuestion.team_1_answer !== null) {
      team1Difference = Math.abs(
        currentQuestion.team_1_answer - currentQuestion.correct_answer,
      ).toString();
    }

    if (currentQuestion.team_2_answer !== null) {
      team2Difference = Math.abs(
        currentQuestion.team_2_answer - currentQuestion.correct_answer,
      ).toString();
    }

    // Determine winner
    if (team1Difference !== "N/A" && team2Difference !== "N/A") {
      if (parseFloat(team1Difference) < parseFloat(team2Difference)) {
        winner = 1;
      } else if (parseFloat(team2Difference) < parseFloat(team1Difference)) {
        winner = 2;
      }
      // If equal, no winner (null)
    }
  }

  const { team1Score, team2Score } = getClosestGuessBonusRoundScoreDB(
    closestGuessQuestions,
  );

  return (
    <Box
      sx={{ width: "100%", display: "flex", flex: 1, justifyContent: "center" }}
      mt="12px"
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 1, sm: 2 },
          mb: 2,
          borderRadius: 2,
          backgroundColor: "#121212",
          color: "#f5f5f5",
          border: "2px solid #913175",
          width: "100%",
          gap: "8px",
        }}
      >
        <Box
          display="flex"
          width="100%"
          justifyContent="space-between"
          pb="8px"
        >
          <Typography
            variant="h5"
            sx={{
              mb: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontWeight: "bold",
              color: "#CD5888",
            }}
          >
            <Speed /> Closest Guess Bonus Round
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1,
              borderRadius: 2,
              bgcolor: "background.paper",
              boxShadow: 1,
            }}
          >
            <Scoreboard color="primary" />
            <Typography variant="subtitle1" color="primary" fontWeight="bold">
              Score:
            </Typography>
            <Chip
              label={team1Score}
              color="primary"
              sx={{
                borderWidth: "2px",
                fontWeight: 700,
              }}
              variant="outlined"
              size={isMobile ? "small" : "medium"}
            />
            <Typography variant="body2" color="primary" fontWeight="medium">
              vs
            </Typography>
            <Chip
              label={team2Score}
              sx={{
                color: "secondary.main",
                borderColor: "secondary.main",
                borderWidth: "2px",
                fontWeight: 700,
              }}
              variant="outlined"
              size={isMobile ? "small" : "medium"}
            />
          </Box>
        </Box>

        <Card
          sx={{
            mb: 2,
            backgroundColor: "#1E1E1E",
            color: "#f5f5f5",
            border: "1px solid #444",
            borderRadius: 2,
          }}
        >
          <CardContent>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              color="#CD5888"
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
            >
              <QuizOutlined /> Question {bonusRound.current_question_idx + 1}:
            </Typography>

            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: "medium",
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
              }}
            >
              {currentQuestion.question_text}
            </Typography>

            {bonusRound.show_answer &&
              currentQuestion.correct_answer !== null && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: "rgba(145, 49, 117, 0.15)",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "#CD5888",
                    }}
                  >
                    <CheckCircleOutline /> Correct Answer:
                  </Typography>
                  <Chip
                    label={currentQuestion.correct_answer.toString()}
                    sx={{
                      fontWeight: "bold",
                      backgroundColor: "#913175",
                      color: "white",
                      fontSize: "1.1rem",
                      height: "auto",
                      py: 0.5,
                    }}
                  />
                </Box>
              )}
          </CardContent>
        </Card>

        {bonusRound.show_answer && (
          <Grid container spacing={3} justifyContent="center">
            {/* Team 1 Answer */}
            <Grid spacing={{ xs: 6, md: 3 }}>
              <Card
                sx={{
                  position: "relative",
                  backgroundColor: "#1E1E1E",
                  color: "#f5f5f5",
                  border: winner === 1 ? "4px solid #913175" : "1px solid #444",
                  borderRadius: 2,
                  transition: "transform 0.2s",
                  transform: winner === 1 ? "scale(1.02)" : "scale(1)",
                }}
              >
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: winner === 1 ? "#CD5888" : "#f5f5f5",
                      fontWeight: "bold",
                      mb: 1,
                    }}
                  >
                    <Person /> {team1Name}
                  </Typography>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Box>
                      <Typography variant="body2" color="#BBB">
                        Guessed:
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                        {currentQuestion.team_1_answer !== null
                          ? currentQuestion.team_1_answer.toString()
                          : "No answer provided"}
                      </Typography>
                    </Box>

                    <Divider
                      sx={{ bgcolor: "rgba(255,255,255,0.1)", my: "4px" }}
                    />

                    <Box>
                      <Typography variant="body2" color="#BBB">
                        Difference from correct answer:
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: winner === 1 ? "#913175" : "white",
                        }}
                      >
                        {team1Difference}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Team 2 Answer */}
            <Grid spacing={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  position: "relative",
                  backgroundColor: "#1E1E1E",
                  color: "#f5f5f5",
                  border: winner === 2 ? "4px solid #CD5888" : "1px solid #444",
                  borderRadius: 2,
                  transition: "transform 0.2s",
                  transform: winner === 2 ? "scale(1.02)" : "scale(1)",
                }}
              >
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: winner === 2 ? "#CD5888" : "#f5f5f5",
                      fontWeight: "bold",
                      mb: 1,
                    }}
                  >
                    <Person2 /> {team2Name}
                  </Typography>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Box>
                      <Typography variant="body2" color="#BBB">
                        Guessed:
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                        {currentQuestion.team_2_answer !== null
                          ? currentQuestion.team_2_answer.toString()
                          : "No answer provided"}
                      </Typography>
                    </Box>

                    <Divider
                      sx={{ bgcolor: "rgba(255,255,255,0.1)", my: "4px" }}
                    />

                    <Box>
                      <Typography variant="body2" color="#BBB">
                        Difference from correct answer:
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: winner === 2 ? "#CD5888" : "white",
                        }}
                      >
                        {team2Difference}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default ClosestGuessBonusRoundPlayer;
